import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/AuthContext";

export default function AuthCallback() {
  const nav = useNavigate();
  const { fetchUser } = useAuth();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) {
      nav("/login");
      return;
    }
    const sessionId = m[1];

    (async () => {
      try {
        // Read (and consume) the invite code stashed on the landing page.
        let inviteCode = null;
        try {
          inviteCode = localStorage.getItem("dsl_invite");
          if (inviteCode) localStorage.removeItem("dsl_invite");
        } catch (_e) { /* ignore */ }
        const body = { session_id: sessionId };
        if (inviteCode) body.invite_code = inviteCode;
        const r = await api.post("/auth/google/session", body);
        if (r.data?.session_token) {
          localStorage.setItem("session_token", r.data.session_token);
        }
        await fetchUser();
        // Clear hash
        window.history.replaceState(null, "", window.location.pathname);
        // Honor stashed redirect target from Login (?next=...). When the
        // user came in cold (no stashed target) drop them straight into the
        // Season One drop schedule instead of the top-of-page welcome hero —
        // shaves a whole scroll+read moment off the onboarding.
        let next = "/#season-one";
        try {
          const stashed = sessionStorage.getItem("post_login_next");
          if (stashed) { next = stashed; sessionStorage.removeItem("post_login_next"); }
          // One-shot flag so the Home page can pop a soft "welcome, Member #NNN"
          // toast on arrival. Read + cleared once by Home.
          sessionStorage.setItem("welcome_toast", "1");
        } catch (err) { console.warn("sessionStorage unavailable:", err); }

        // First-time gate: if the user has no shipping address yet AND they
        // were trying to enter a live room, take them through profile setup.
        // This prevents landing in a live room without being able to buy.
        const needsProfile = next.startsWith("/live/");
        if (needsProfile) {
          try {
            const addr = await api.get("/addresses");
            const hasAddress = Array.isArray(addr.data) && addr.data.length > 0;
            if (!hasAddress) {
              nav(`/profile?complete=1&next=${encodeURIComponent(next)}`, { replace: true });
              return;
            }
          } catch (addrErr) {
            console.warn("Could not fetch addresses for gate:", addrErr);
          }
        }
        nav(next, { replace: true });
      } catch (e) {
        console.error("Auth callback failed", e);
        nav("/login", { replace: true });
      }
    })();
  }, [nav, fetchUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8F7F2] px-6" data-testid="auth-callback">
      <div className="text-center max-w-md">
        <div className="brand-tag mb-4">// Welcome home</div>
        <div className="font-display text-3xl md:text-4xl uppercase tracking-tight leading-tight text-[#161616]">
          Assigning your <span className="text-outline">member number</span>…
        </div>
        {/* One-heartbeat pulse — the whole exchange usually resolves inside 400ms
            and the nav takes over. Kept minimal so it flashes past. */}
        <div className="mt-8 mx-auto w-2.5 h-2.5 rounded-full bg-[#FF6A00] animate-ping" aria-hidden="true" />
      </div>
    </div>
  );
}
